import React from "react";
import { useTranslation } from "react-i18next";

import styles from "./Table.module.scss";

import CurrencyInput from "@/components/forms/inputs/CurrencyInput";
import Icons from "@/components/icons";
import { TableColumn } from "@/types/table/tableProps";

interface TableProps<T> {
  columns: TableColumn[];
  data: T[];
  direction?: "asc" | "desc";
  editableFields?: Array<keyof T>;
  isEditMode?: boolean;
  isLoading?: boolean;
  onFieldChange?: (rowIndex: number, field: keyof T, value: unknown) => void;
  onRowClicked?: (row: T) => void;
  onSort?: (column: string) => void;
  orderBy?: string | null;
}

const Table = <T, >({
  columns,
  data,
  direction,
  editableFields = [],
  isEditMode = false,
  isLoading,
  onFieldChange,
  onRowClicked,
  onSort,
  orderBy,
}: TableProps<T>) => {
  const { t } = useTranslation();
  const handleSort = (column: string) => {
    onSort?.(column);
  };

  const updatedColumns = columns.map((column) => ({
    ...column,
    sortable: column.sortable ?? true,
  }));

  const renderEditableCell = (
    row: T,
    rowIndex: number,
    column: TableColumn<T>
  ) => {
    if (!column.accessor) return null;

    const accessor = column.accessor as keyof T;
    const rawValue = row[accessor];

    const handleChange = (value: number | undefined) => {
      onFieldChange?.(rowIndex, accessor, value);
    };

    return (
      <CurrencyInput
        disabled={isLoading}
        value={rawValue === null ? undefined : Number(rawValue)}
        onChange={handleChange}
        inputStyle="table"
        withDecimals={true}
      />
    );
  };

  return (
    <div className={styles.tableContainer}>
      <table>
        <thead>
          <tr>
            {updatedColumns.map((column, index) => {
              return (
                <th
                  key={`${column.accessor}-${index}`}
                  scope="col"
                  className={`${styles.headerPointer} ${
                    column.textAlign === "right" ? styles.alignRight : ""
                  }`}
                  style={{
                    width: column.width ? `${column.width}` : "100%",
                  }}
                  onClick={() => {
                    if (column.sortable !== false && column.accessor) {
                      handleSort(column.accessor);
                    }
                  }}
                >
                  <span className={`${styles.headerWrapper}`}>
                    {column.header}
                    {orderBy && orderBy === column.accessor && (
                      <span>
                        {direction === "desc" ? (
                          <Icons.ArrowDownIcon fontSize={24} />
                        ) : (
                          <Icons.ArrowUpIcon fontSize={24} />
                        )}
                      </span>
                    )}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <Skeleton columns={columns} totalRows={10} />
          ) : data === undefined || data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className={styles.noData}>
                {t("pagination.noItems")}
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                onClick={() => onRowClicked && onRowClicked(row)}
              >
                {columns.map((column, colIndex) => (
                  <td
                    key={colIndex}
                    className={
                      isEditMode &&
                      column.accessor &&
                      editableFields.includes(column.accessor as keyof T)
                        ? styles.editCell
                        : undefined
                    }
                  >
                    {isEditMode &&
                    column.accessor &&
                    editableFields.includes(column.accessor as keyof T)
                      ? renderEditableCell(row, rowIndex, column)
                      : column.Cell
                        ? column.Cell(row, rowIndex)
                        : String(row[column.accessor as keyof typeof row] ?? "")}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default Table;

interface TableSkeletonProps {
  totalRows: number;
  columns: TableColumn[];
}

const Skeleton = ({ totalRows, columns }: TableSkeletonProps) => {
  return Array.from({ length: totalRows }).map((_, rowIndex) => (
    <tr key={`skeleton-row-${rowIndex}`} className={styles.skeletonRow}>
      {columns.map((column, colIndex) => (
        <React.Fragment key={`skeleton-row-${rowIndex}-col-${colIndex}`}>
          {column.accessor !== "spacer" ? (
            <td
              className={`${styles.skeletonCell}  ${
                column.accessor === "actions" && styles.actionCell
              }`}
              style={{
                width: column.width ? `${column.width}` : "100%",
              }}
            >
              <div></div>
            </td>
          ) : (
            <td></td>
          )}
        </React.Fragment>
      ))}
    </tr>
  ));
};
